import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { type RootState } from '@/store';
// Straight from the slice, not the `@/store` barrel: the barrel imports this
// file, and only the type import above is erased at build time.
import { logout } from '@/store/slices/authSlice';

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.accessToken;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    // White-label: tell the backend which brand domain this app is served on.
    if (typeof window !== 'undefined') {
      headers.set('X-Brand-Domain', window.location.hostname);
    }
    return headers;
  },
});

/**
 * A 401 means the session is over — end it here, in full and at once.
 *
 * The old version removed the cookie but left the store still saying
 * "authenticated", then hard-navigated on a two second timer. For those two
 * seconds the operator sat in front of a complete dashboard with every figure
 * reading zero, which is the state that gets reported as "the panel opened
 * without asking me to log in".
 *
 * Dispatching `logout` clears the cookie AND the store, so the guards flip
 * immediately whether or not the redirect lands. The /login check matters: a
 * mistyped password answers 401 too, and bouncing the login page off itself
 * would throw away the error the operator needs to read.
 */
const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  const result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    api.dispatch(logout());
    if (window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
  }
  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 0,
  tagTypes: [
    'User',
    'Auth',
    'FeatureFlags',
    'Common',
    'Account',
    'Upi',
    'Crypto',
    'FD',
    'DiamondEvents',
    'DiamondMarkets',
    'Requests',
    'GetNotification',
    'Members',
    'GetBanner',
    'Casino',
    'Getsupport',
    'Event',
    'Market',
    'BetConfig',
    'Affiliates',
    'AffiliateSlabs',
    'AffiliateSlabHistory',
    'SystemSettings',
    'Wallet',
    'Provider',
    'BalanceSummary',
    'UserManagement',
    'GameAnalytics',
    'CasinoAnalytics',
    'BusinessAnalytics',
    'TopUsers',
    'TopCategories',
    'DeviceBreakdown',
    'BonusAnalytics',
    'LoginSummary',
    'LiveGames',

    'Bonus',
    'BonusApplicants',
    'Brands',
  ],
  endpoints: () => ({}),
});

// Export hooks for usage in functional components
export const {
  util: { getRunningQueriesThunk },
} = apiSlice;

// Export the api slice for use in store
export default apiSlice;
